import { useRef, useState } from "react";
import { CheckCheck, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, PrimaryButtonClass } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const FORMATS = [
  ["auto", "Detect automatically"],
  ["csv", "CSV / delimited"],
  ["ofx", "OFX / QFX"],
  ["mt940", "SWIFT MT940"],
];

const pad = (n) => String(n).padStart(2, "0");

const buildSample = (format, entries) => {
  const rows = entries.slice(0, 4);
  if (rows.length === 0) return { name: "", body: "" };
  if (format === "ofx") {
    const body = `OFXHEADER:100\nDATA:OFXSGML\n<OFX><BANKMSGSRSV1><STMTTRNRS><STMTRS><BANKTRANLIST>\n${rows
      .map((r) => {
        const d = r.entry_date.replaceAll("-", "");
        return `<STMTTRN><TRNTYPE>${r.direction === "credit" ? "CREDIT" : "DEBIT"}<DTPOSTED>${d}<TRNAMT>${
          r.direction === "credit" ? "" : "-"}${r.base_amount.toFixed(2)}<FITID>REF${r.id.slice(-6)}<MEMO>${
          (r.description || "").slice(0, 40)}</STMTTRN>`;
      })
      .join("\n")}\n</BANKTRANLIST></STMTRS></STMTTRNRS></BANKMSGSRSV1></OFX>`;
    return { name: "sample-statement.ofx", body };
  }
  if (format === "mt940") {
    const body = `:20:SERVECOR-DEMO\n:25:HDFC/0001234567\n:28C:00042/001\n${rows
      .map((r) => {
        const d = new Date(r.entry_date);
        const stamp = `${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
        return `:61:${stamp}${r.direction === "credit" ? "C" : "D"}${r.base_amount
          .toFixed(2)
          .replace(".", ",")}NTRFREF${r.id.slice(-6)}\n:86:${(r.description || "").slice(0, 60)}`;
      })
      .join("\n")}`;
    return { name: "sample-statement.sta", body };
  }
  const body = `value_date,description,reference,debit,credit\n${rows
    .map((r) => `${r.entry_date},"${(r.description || "").replaceAll('"', "")}",REF${r.id.slice(-6)},${
      r.direction === "debit" ? r.base_amount.toFixed(2) : ""},${
      r.direction === "credit" ? r.base_amount.toFixed(2) : ""}`)
    .join("\n")}`;
  return { name: "sample-statement.csv", body };
};

export const StatementImport = ({ onReconciled }) => {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [fileName, setFileName] = useState("");
  const [content, setContent] = useState("");
  const [fmt, setFmt] = useState("auto");
  const [account, setAccount] = useState("HDFC Primary current account");
  const [tolerance, setTolerance] = useState(5);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setContent(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runImport = async () => {
    setBusy("import");
    setResult(null);
    try {
      const { data } = await api.post("/charity/statements/upload", {
        filename: fileName || "statement.csv",
        content,
        file_format: fmt,
        account_label: account,
        tolerance_days: Number(tolerance) || 5,
      });
      setResult(data);
      setSelected(Object.fromEntries(data.matched.map((m) => [m.entry_id, true])));
      toast.success(`${data.file_format.toUpperCase()} parsed — ${data.matched.length} of ${data.total_lines} lines matched`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const confirm = async () => {
    setBusy("confirm");
    try {
      const pairs = result.matched
        .filter((m) => selected[m.entry_id])
        .map((m) => ({ entry_id: m.entry_id, bank_reference: m.line.bank_reference || "" }));
      const { data } = await api.post("/charity/statements/confirm", { pairs });
      toast.success(`${data.reconciled} ledger entries reconciled`);
      setOpen(false);
      setResult(null);
      setContent("");
      setFileName("");
      await onReconciled?.();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const selectedCount = result ? result.matched.filter((m) => selected[m.entry_id]).length : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-statement-button" className="rounded-full">
          <Upload className="mr-1.5 h-4 w-4" /> Import statement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import a bank statement</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            CSV, OFX/QFX and SWIFT MT940 are supported. Lines are matched to open ledger entries on amount,
            direction and value date within the tolerance window — nothing is reconciled until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-[11px] text-muted-foreground">Statement file</Label>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept=".csv,.txt,.ofx,.qfx,.sta,.mt940,.940"
                     onChange={(e) => readFile(e.target.files?.[0])} className="hidden"
                     data-testid="statement-file-input" />
              <Button type="button" variant="outline" className="rounded-full"
                      onClick={() => fileRef.current?.click()} data-testid="statement-choose-file">
                <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Choose file
              </Button>
              <span className="font-mono text-[11px] text-muted-foreground" data-testid="statement-file-name">
                {fileName || "no file selected"}
              </span>
              <button type="button" data-testid="statement-use-sample"
                      onClick={async () => {
                        try {
                          const { data } = await api.get("/charity/ledger");
                          const open = data.filter((r) => !r.reconciled);
                          const sample = buildSample(fmt === "auto" ? "csv" : fmt, open);
                          if (!sample.body) { toast.error("No open ledger entries to build a sample from"); return; }
                          setContent(sample.body);
                          setFileName(sample.name);
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                      className="text-[11px] text-primary hover:underline">
                generate sample from open entries
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Format</Label>
            <Select value={fmt} onValueChange={setFmt}>
              <SelectTrigger data-testid="statement-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Bank account label</Label>
            <Input value={account} onChange={(e) => setAccount(e.target.value)} data-testid="statement-account-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Date tolerance (days)</Label>
            <Input type="number" min={0} max={30} value={tolerance}
                   onChange={(e) => setTolerance(e.target.value)} data-testid="statement-tolerance-input"
                   className="font-mono" />
          </div>
          <div className="flex items-end">
            <Button onClick={runImport} disabled={busy === "import" || !content.trim()}
                    data-testid="statement-parse-button" className={PrimaryButtonClass}>
              {busy === "import" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Parse &amp; match
            </Button>
          </div>
        </div>

        {result && (
          <div className="space-y-4" data-testid="statement-result">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ["Format", result.file_format.toUpperCase(), "statement-kpi-format"],
                ["Lines read", result.total_lines, "statement-kpi-lines"],
                ["Matched", result.matched.length, "statement-kpi-matched"],
                ["Matched value", money(result.matched_value, "INR", true), "statement-kpi-value"],
              ].map(([label, value, testId]) => (
                <div key={label} className="rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid={testId}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground">{value}</div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-border">
              <div className="border-b border-border px-3 py-2 text-xs font-semibold text-foreground">
                Proposed matches
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-border">
                {result.matched.length === 0 && (
                  <div className="px-3 py-6 text-xs text-muted-foreground">Nothing matched — check the value dates and tolerance.</div>
                )}
                {result.matched.map((m) => (
                  <label key={m.entry_id} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-muted/50"
                         data-testid={`statement-match-${m.entry_id}`}>
                    <input type="checkbox" checked={Boolean(selected[m.entry_id])}
                           onChange={(e) => setSelected({ ...selected, [m.entry_id]: e.target.checked })}
                           data-testid={`statement-match-checkbox-${m.entry_id}`}
                           className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs text-foreground">{m.line.description || "(no narrative)"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {shortDate(m.line.value_date)} · {m.line.direction} · {m.line.bank_reference || "no ref"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-xs text-foreground">{money(m.line.amount, "INR")}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {m.entry.fund_code || "—"} · {m.entry.account_code}
                      </div>
                    </div>
                    <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                      {m.confidence}%
                    </Chip>
                  </label>
                ))}
              </div>
            </div>

            {result.unmatched.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="statement-unmatched">
                <div className="border-b border-amber-500/20 px-3 py-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                  {result.unmatched.length} unmatched line{result.unmatched.length > 1 ? "s" : ""}
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-amber-500/10">
                  {result.unmatched.map((u, i) => (
                    <div key={`${u.line.value_date}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                      <span className="min-w-0 flex-1 truncate text-foreground/80">
                        {shortDate(u.line.value_date)} · {u.line.description || "(no narrative)"}
                      </span>
                      <span className="font-mono text-foreground">{money(u.line.amount, "INR")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={confirm} disabled={busy === "confirm" || selectedCount === 0}
                    data-testid="statement-confirm-button" className={PrimaryButtonClass}>
              {busy === "confirm"
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <CheckCheck className="mr-1.5 h-4 w-4" />}
              Reconcile {selectedCount} entr{selectedCount === 1 ? "y" : "ies"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
